"use client";

// Popup som visar en lista av repetitions-delavsnitt. Eleven klickar på ett kort
// för att öppna repetitionsuppgifterna och lämnar in dem där
// (/elev/repetition/[slug]/[intervall]). Det finns ingen manuell bockning: en
// repetition räknas som gjord ENDAST när uppgifterna gjorts och lämnats in.
//
// Raden visar bara VAD som ska repeteras. Slot-etiketten (Nyligen/Senare/Långt
// sedan) är en schemaläggningsdetalj som hör hemma hos läraren och visas inte —
// intervallet lever kvar i URL:en och i DB. Inlämnade kort är inte klickbara och
// renderas som <div>, inte <button disabled>: de är statusinformation, inte
// inaktiverade kontroller, och ska varken ta fokus eller läsas upp som knappar.
//
// Används från:
//   1. RP-ikon i LektionsBarForElev (visar aktuell lektions reps)
//   2. "Att göra"-rutan på elev-landningssidan (visar alla rep-DA från
//      elevens klasser någonsin)

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import {
  repTillfalleEtikett,
  sorteraRepEfterSlot,
  type RepIntervall,
} from "@/lib/repetition/slots";

export interface RepListaItem {
  // Trippeln behövs för att rep-sidan ska kunna bocka rätt delavsnitt (bar slug
  // är inte unik per kurs).
  kapitelId: string;
  avsnittId: string;
  slug: string;
  namn: string;
  kapitelNamn: string;
  avsnittNamn: string;
  intervall: RepIntervall;
  /** Inlämnad — eleven har gjort och lämnat in repetitionen. */
  gjord: boolean;
}

interface Props {
  titel: string;
  items: RepListaItem[];
  onClose: () => void;
}

/** Identitet för ett delavsnitt — trippeln, eftersom bar slug inte är unik. */
function delavsnittNyckel(i: RepListaItem): string {
  return `${i.kapitelId}::${i.avsnittId}::${i.slug}`;
}

/** Var repetitionen hör hemma. Enda kontexten som står kvar på kortet. */
function Harkomst({ item }: { item: RepListaItem }) {
  return (
    <span className="block text-[11px] text-[var(--color-text-muted)] mt-0.5">
      {item.kapitelNamn} › {item.avsnittNamn}
    </span>
  );
}

/**
 * "Repetition 1 av 2" — bara när samma delavsnitt förekommer flera gånger i
 * listan. Ett delavsnitt kan ha flera repetitionstillfällen igång samtidigt,
 * och utan skiljetecken blir korten identiska: samma namn, samma kapitel, två
 * knappar. Etiketten säger vilket tillfälle i ordningen det är, inte när det
 * var tänkt — en elev ska inte behöva tolka "Nyligen" mot ett schema hon inte
 * ser. Samma funktion som lärarens vyer använder, så en rad aldrig heter en
 * sak för eleven och en annan för läraren.
 */
function Ordinal({ intervall }: { intervall: RepIntervall }) {
  return (
    <span className="text-[10px] font-mono uppercase tracking-wider shrink-0 text-[var(--color-text-muted)]">
      Repetition {repTillfalleEtikett(intervall)}
    </span>
  );
}

function RepKortAttGora({
  item,
  visaOrdinal,
  onOpen,
}: {
  item: RepListaItem;
  visaOrdinal: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Öppna repetitionsuppgifterna och lämna in dem när du är klar"
      className="w-full text-left rounded-md border px-4 py-3 transition-colors border-[var(--color-border)] bg-[var(--color-bg-card)] hover:bg-[var(--color-bg)] hover:border-[var(--color-accent)]"
    >
      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-[15px] font-medium text-[var(--color-text)]">
          {item.namn}
        </span>
        {visaOrdinal && (
          <span className="ml-auto">
            <Ordinal intervall={item.intervall} />
          </span>
        )}
      </span>
      <Harkomst item={item} />
    </button>
  );
}

function RepKortInlamnat({
  item,
  visaOrdinal,
}: {
  item: RepListaItem;
  visaOrdinal: boolean;
}) {
  return (
    <div className="w-full rounded-md border px-4 py-3 border-[var(--color-figur-salvia)] bg-[var(--color-figur-salvia)]/20">
      {/* flex-wrap: på mobil får märkena falla ned på egen rad i stället för
          att lägga sig ovanpå delavsnittsnamnet. */}
      <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          aria-hidden="true"
          className="text-[var(--color-figur-mossa)] text-sm leading-none"
        >
          ✓
        </span>
        <span className="text-[15px] font-medium text-[var(--color-text)]">
          {item.namn}
        </span>
        <span className="ml-auto flex items-baseline gap-3">
          {visaOrdinal && <Ordinal intervall={item.intervall} />}
          <span
            className="text-[10px] font-mono uppercase tracking-wider"
            style={{ color: "var(--color-figur-mossa)" }}
          >
            Inlämnad
          </span>
        </span>
      </span>
      <Harkomst item={item} />
    </div>
  );
}

export function RepListaModal({ titel, items, onClose }: Props) {
  const router = useRouter();

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    document.body.dataset.modalOpen = "true";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      delete document.body.dataset.modalOpen;
    };
  }, [onClose]);

  function open(item: RepListaItem) {
    onClose();
    const q = new URLSearchParams({ k: item.kapitelId, a: item.avsnittId });
    router.push(`/elev/repetition/${item.slug}/${item.intervall}?${q.toString()}`);
  }

  // Sortera: ogjorda först, sedan gjorda — inom varje grupp i tidsordning.
  const sorted = [
    ...sorteraRepEfterSlot(items.filter((i) => !i.gjord)),
    ...sorteraRepEfterSlot(items.filter((i) => i.gjord)),
  ];

  // Delavsnitt som har mer än ett tillfälle i listan — bara de får etiketten.
  // Trippeln, inte bar slug: samma slug finns i flera kurser (BUG-2026-031),
  // och två olika delavsnitt ska inte numreras som om de vore samma.
  const antalPerDelavsnitt = new Map<string, number>();
  for (const i of items) {
    const k = delavsnittNyckel(i);
    antalPerDelavsnitt.set(k, (antalPerDelavsnitt.get(k) ?? 0) + 1);
  }

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={titel}
    >
      <div
        className="rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "var(--color-bg-card)" }}
      >
        <div className="px-5 py-4 border-b border-[var(--color-border)] flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium text-[var(--color-primary)]">
              {titel}
            </h2>
            <p className="text-sm text-[var(--color-text-muted)] mt-1">
              Öppna och gör uppgifterna — lämna sedan in repetitionen.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Stäng (Esc)"
            aria-label="Stäng"
            className="shrink-0 w-8 h-8 rounded-full text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg)] transition-colors flex items-center justify-center text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {sorted.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)] italic">
              Inga repetitioner att visa.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {sorted.map((item) => {
                const nyckel = delavsnittNyckel(item);
                const visaOrdinal = (antalPerDelavsnitt.get(nyckel) ?? 0) > 1;
                return (
                  <li key={`${nyckel}::${item.intervall}`}>
                    {item.gjord ? (
                      <RepKortInlamnat item={item} visaOrdinal={visaOrdinal} />
                    ) : (
                      <RepKortAttGora
                        item={item}
                        visaOrdinal={visaOrdinal}
                        onOpen={() => open(item)}
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
